import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 2, retry: 1 } },
});
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import SellerOnlyRoute from './components/layout/SellerOnlyRoute';
import CookieBanner from './components/ui/CookieBanner';
import CommandPalette from './components/ui/CommandPalette';
import { ToastProvider } from './components/ui/Toast';
import Callback from './components/auth/Callback';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import { useProfile } from './hooks/useUserData';
import { usePageMeta } from './hooks/usePageMeta';
import { useCookieStore } from './store/cookieStore';
import { loadAnalytics, unloadAnalytics, trackPageview } from './lib/analytics';

const HomePage = lazy(() => import('./pages/HomePage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPageLazy = lazy(() =>
  import('./pages/LoginPage').then(m => ({ default: m.SignupPage as React.ComponentType }))
);
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const SellerGuidePage = lazy(() => import('./pages/SellerGuidePage'));
const SellerProfilePage = lazy(() => import('./pages/SellerProfilePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PaymentComingSoon = lazy(() => import('./pages/PaymentComingSoon'));
const NewProductPage = lazy(() => import('./pages/dashboard/NewProductPage'));
const ListingsPage = lazy(() => import('./pages/dashboard/ListingsPage'));
const AdminPage        = lazy(() => import('./pages/AdminPage'));
const SupportPage      = lazy(() => import('./pages/SupportPage'));
const TermsPage        = lazy(() => import('./pages/LegalPage').then(m => ({ default: m.TermsPage as React.ComponentType })));
const PrivacyPage      = lazy(() => import('./pages/LegalPage').then(m => ({ default: m.PrivacyPage as React.ComponentType })));
const AUPPage          = lazy(() => import('./pages/LegalPage').then(m => ({ default: m.AUPPage as React.ComponentType })));
const RefundPolicyPage = lazy(() => import('./pages/LegalPage').then(m => ({ default: m.RefundPolicyPage as React.ComponentType })));
const DMCAPage         = lazy(() => import('./pages/LegalPage').then(m => ({ default: m.DMCAPage as React.ComponentType })));
const AccountLayout    = lazy(() => import('./pages/account/AccountLayout'));
const DashboardLayout  = lazy(() => import('./components/layout/DashboardLayout'));
const OrdersPage       = lazy(() => import('./pages/account/OrdersPage'));
const DisputesPage     = lazy(() => import('./pages/account/DisputesPage'));
const WishlistPage     = lazy(() => import('./pages/account/WishlistPage'));
const FollowingPage    = lazy(() => import('./pages/account/FollowingPage'));
const PromoCodesPage   = lazy(() => import('./pages/dashboard/PromoCodesPage'));
const PromotePage      = lazy(() => import('./pages/dashboard/PromotePage'));
const LaunchKitPage    = lazy(() => import('./pages/dashboard/LaunchKitPage'));
const AiphraJsonPage    = lazy(() => import('./pages/AiphraJsonPage'));
const DocsPage         = lazy(() => import('./pages/DocsPage'));
const ConfirmEmailPage = lazy(() => import('./pages/ConfirmEmailPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardHome = lazy(() => import('./pages/dashboard/DashboardHome'));
const ProductsPage = lazy(() => import('./pages/dashboard/ProductsPage'));
const WalletPage = lazy(() => import('./pages/dashboard/WalletPage'));
const AnalyticsPage = lazy(() => import('./pages/dashboard/AnalyticsPage'));
const StorefrontPage = lazy(() => import('./pages/dashboard/StorefrontPage'));
const PayoutsPage = lazy(() => import('./pages/dashboard/PayoutsPage'));
const SubscriptionPage = lazy(() => import('./pages/dashboard/SubscriptionPage'));
const NotificationsPage = lazy(() => import('./pages/dashboard/NotificationsPage'));
const SettingsPage     = lazy(() => import('./pages/dashboard/SettingsPage'));
const EditProductPage  = lazy(() => import('./pages/dashboard/EditProductPage'));

function PageLoader() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        border: '2px solid rgba(138,92,246,0.2)',
        borderTopColor: '#8A5CF6',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

/** Loads/unloads Plausible strictly according to the visitor's cookie-consent
 *  choice, and fires a pageview on every client-side route change (the script
 *  loads with `data-manual`, so it never auto-fires on its own). */
function AnalyticsController() {
  const analyticsAllowed = useCookieStore(s => s.preferences.analytics);
  const { pathname } = useLocation();

  useEffect(() => {
    if (analyticsAllowed) loadAnalytics();
    else unloadAnalytics();
  }, [analyticsAllowed]);

  useEffect(() => {
    if (analyticsAllowed) trackPageview();
  }, [pathname, analyticsAllowed]);

  return null;
}

function CommandPaletteController() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return open ? <CommandPalette onClose={() => setOpen(false)} /> : null;
}

function MarketplaceLayout({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useAuth();
  const topOffset = isLoggedIn ? 56 + 104 : 104;
  return (
    <>
      <Navbar />
      <main id="main-content" style={{ paddingTop: topOffset }}>{children}</main>
      <Footer />
    </>
  );
}

// Redirects logged-in users to /dashboard — used for the homepage, /login, /signup
function PublicOnly({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useAuth();
  if (isLoggedIn) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Sends signed-out users to /login — used for /dashboard and /account
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// One-time detour to /onboarding for a user who signed up but hasn't picked a
// plan yet. Doesn't block signup itself — this only gates the dashboard/account
// areas afterward.
function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { userId } = useAuth();
  const { data: profile } = useProfile(userId);
  if (profile && !profile.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ToastProvider>
    <BrowserRouter>
      {/* Skip navigation — accessibility */}
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <ScrollToTop />
      <AnalyticsController />
      <CommandPaletteController />
      <CookieBanner />
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<PublicOnly><MarketplaceLayout><HomePage /></MarketplaceLayout></PublicOnly>} />
          <Route path="/explore" element={<MarketplaceLayout><ExplorePage /></MarketplaceLayout>} />
          <Route path="/product/:slug" element={<MarketplaceLayout><ProductPage /></MarketplaceLayout>} />
          <Route path="/pricing" element={<MarketplaceLayout><PricingPage /></MarketplaceLayout>} />

          <Route path="/sellers" element={<MarketplaceLayout><SellerGuidePage /></MarketplaceLayout>} />
          <Route path="/seller/:username" element={<MarketplaceLayout><SellerProfilePage /></MarketplaceLayout>} />
          <Route path="/checkout" element={<MarketplaceLayout><CheckoutPage /></MarketplaceLayout>} />
          <Route path="/payment-coming-soon" element={<MarketplaceLayout><PaymentComingSoon /></MarketplaceLayout>} />

          <Route path="/admin" element={<AdminPage />} />
          <Route path="/support"    element={<MarketplaceLayout><SupportPage /></MarketplaceLayout>} />
          <Route path="/aiphra-json" element={<MarketplaceLayout><AiphraJsonPage /></MarketplaceLayout>} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/terms"         element={<MarketplaceLayout><TermsPage /></MarketplaceLayout>} />
          <Route path="/privacy"       element={<MarketplaceLayout><PrivacyPage /></MarketplaceLayout>} />
          <Route path="/aup"           element={<MarketplaceLayout><AUPPage /></MarketplaceLayout>} />
          <Route path="/refund-policy" element={<MarketplaceLayout><RefundPolicyPage /></MarketplaceLayout>} />
          <Route path="/dmca"          element={<MarketplaceLayout><DMCAPage /></MarketplaceLayout>} />

          <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />

          {/* Buyer account section */}
          <Route path="/account" element={<RequireAuth><RequireOnboarding><AccountLayout /></RequireOnboarding></RequireAuth>}>
            <Route path="purchases"  element={<OrdersPage />} />
            <Route path="disputes"   element={<DisputesPage />} />
            <Route path="wishlist"   element={<WishlistPage />} />
            <Route path="following"  element={<FollowingPage />} />
          </Route>

          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/signup" element={<PublicOnly><SignupPageLazy /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route path="/dashboard" element={<RequireAuth><RequireOnboarding><DashboardLayout /></RequireOnboarding></RequireAuth>}>
            <Route index element={<DashboardHome />} />
            <Route path="listings"     element={<SellerOnlyRoute><ListingsPage /></SellerOnlyRoute>} />
            <Route path="promote"      element={<SellerOnlyRoute><PromotePage /></SellerOnlyRoute>} />
            <Route path="launch-kit"   element={<LaunchKitPage />} />
            <Route path="promo-codes" element={<PromoCodesPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/new" element={<NewProductPage />} />
            <Route path="products/:id" element={<EditProductPage />} />
            <Route path="storefront" element={<StorefrontPage />} />
            <Route path="analytics" element={<SellerOnlyRoute><AnalyticsPage /></SellerOnlyRoute>} />
            <Route path="wallet" element={<SellerOnlyRoute><WalletPage /></SellerOnlyRoute>} />
            <Route path="payouts" element={<SellerOnlyRoute><PayoutsPage /></SellerOnlyRoute>} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* OAuth callback — must be outside auth guards */}
          <Route path="/callback" element={<Callback />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />

          <Route path="*" element={<MarketplaceLayout><NotFound /></MarketplaceLayout>} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
    </ToastProvider>
    </QueryClientProvider>
  );
}

function NotFound() {
  // The SPA can't return a true HTTP 404 status (Amplify serves this same
  // shell, with a 200, for any unmatched path) — but it can and must stop the
  // page from looking like a duplicate of the homepage to crawlers that do
  // execute JS. Without this, an unmatched URL inherited whatever meta/
  // canonical was last set (often the homepage's), which is a soft-404.
  usePageMeta({ title: 'Page not found', noindex: true, canonicalPath: window.location.pathname });

  return (
    <div style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 96, color: 'rgba(138,92,246,0.12)', lineHeight: 1, fontWeight: 700 }}>404</div>
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 32, color: 'var(--text-primary)', fontWeight: 700 }}>Not in the vault.</h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 16 }}>The page you're looking for doesn't exist.</p>
      <a href="/" style={{ color: 'var(--accent-primary)', textDecoration: 'none', fontSize: 14 }}>← Back to Aiphra</a>
    </div>
  );
}
